import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  Box,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import BillDetailsComponent from "./BillDetailsComponent";
import { fetchBill, getBillerDetails, payBill } from "./apiServices";
import { red } from "@mui/material/colors";

const GenericUtilityService = ({
  billers,
  serviceName,
  loadMoreBillers,
  hasMoreBillers,
  isLoading,
}) => {
  const [parameters, setParameters] = useState([]);
  const [inputParameters, setInputValues] = useState({});
  const [selectedBiller, setSelectedBiller] = useState("");
  const [billerId, setBillerId] = useState("");
  const [billDetails, setBillDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [billFetch, setBillFetch] = useState(true);
  const [validateBiller, setValidateBiller] = useState(false);
  const [transactionAmount, setTransactionAmount] = useState(0);
  const [initChannels, setInitChannels] = useState("");
  const [paymentModes, setPaymentModes] = useState("");
  const [enquiryReferenceId, setEnquiryReferenceId] = useState("");
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const menuRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(false);

  const filteredBillers = billers.filter((biller) =>
    biller.billerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBillerChange = async (selectedValue) => {
    setSelectedBiller(selectedValue);
    setSearchTerm("");
    setInputValues({});

    const selectedBillerDetails = billers.find(
      (biller) => biller.billerName === selectedValue
    );

    if (selectedBillerDetails) {
      setBillerId(selectedBillerDetails.billerId);
      const response = await getBillerDetails(selectedBillerDetails.billerId);

      if (response?.body) {
        const { parameters, billFetch, validateBiller, initChannels, paymentModes } =
          response.body;
        setParameters(parameters);
        setBillFetch(billFetch);
        setValidateBiller(validateBiller);
        setInitChannels(initChannels);
        setPaymentModes(paymentModes);
      } else {
        toast.error("No parameters found for the selected biller.");
      }
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const currentIsNearBottom = scrollHeight - (scrollTop + clientHeight) < 50;
    setIsNearBottom(currentIsNearBottom);

    if (currentIsNearBottom && hasMoreBillers && !isLoading && !isScrolling) {
      setIsScrolling(true);
      loadMoreBillers();
      setTimeout(() => setIsScrolling(false), 1000);
    }
  };

  useEffect(() => {
    if (filteredBillers.length === 0 && hasMoreBillers && !isLoading) {
      loadMoreBillers();
    }
  }, [filteredBillers, hasMoreBillers, isLoading, loadMoreBillers]);

  const handleProceedToRecharge = async () => {
    setLoading(true);
    const response = await fetchBill(
      billerId,
      inputParameters,
      initChannels,
      paymentModes,
      transactionAmount
    );
    setLoading(false);

    if (response?.statusCodeValue === 200) {
      setBillDetails(response.body.data);
      setEnquiryReferenceId(response.body.data.enquiryReferenceId || "");
      toast.success("Bill details fetched successfully");
    } else {
      setMessage(response.body.message);
      toast.error(response.body.message || "Failed to fetch bill details");
    }
  };

  const handlePayNow = async () => {
    setLoading(true);
    const response = await payBill(
      billerId,
      inputParameters,
      transactionAmount,
      initChannels,
      paymentModes,
      enquiryReferenceId,
      validateBiller,
      serviceName
    );
    setLoading(false);

    if (response?.body?.statusCodeValue === 200) {
      toast.success(response.body.response?.status || response.body.message || "Payment successful");
    } else {
      toast.error(response?.body?.response?.status || response?.body?.message || "Payment failed");
    }
  };

  return (
    <Box style={{ padding: "2rem" }}>
      <ToastContainer />
      <Grid container spacing={2}>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            label={`Search and Select ${serviceName} Provider`}
            value={selectedBiller}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (!e.target.value) {
                setSelectedBiller("");
              }
            }}
            onFocus={() => setSearchTerm("")}
            select
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 500,
                    width: 400,
                    overflowX: 'auto',
                    overflowY: 'auto',
                  },
                  onScroll: handleScroll,
                },
              },
              renderValue: (selected) => (
                <Box sx={{ 
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%'
                }}>
                  {selected || `Select ${serviceName} Provider`}
                </Box>
              )
            }}
          >
            {filteredBillers.map((biller) => (
              <MenuItem
                key={`${biller.billerId}-${biller.id}`}
                value={biller.billerName}
                onClick={() => handleBillerChange(biller.billerName)}
              >
                <Box display="flex" alignItems="center" minWidth={0}>
                  <Avatar
                    src={biller.iconUrl}
                    alt={biller.billerName}
                    sx={{ width: 24, height: 24, marginRight: 1 }}
                  />
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {biller.billerName}
                  </Box>
                </Box>
              </MenuItem>
            ))}

            {isLoading && (
              <MenuItem disabled>
                <Box display="flex" justifyContent="center" width="100%">
                  <CircularProgress size={24} />
                </Box>
              </MenuItem>
            )}

            {!hasMoreBillers && filteredBillers.length > 0 && (
              <MenuItem disabled sx={{ p: 0 }}>
                <Box
                  display="flex"
                  justifyContent="center"
                  width="100%"
                  sx={{
                    color: '#fff',
                    background: 'linear-gradient(45deg, #ff5252 0%, #ff1744 100%)',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    fontWeight: '700',
                    fontSize: '0.875rem',
                    boxShadow: '0 3px 5px rgba(211, 47, 47, 0.3)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    margin: '6px 12px',
                    textAlign: 'center',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.02)' },
                      '100%': { transform: 'scale(1)' }
                    }
                  }}
                >
                  No more billers available
                </Box>
              </MenuItem>
            )}

            {filteredBillers.length === 0 && !isLoading && (
              <MenuItem disabled>
                No billers found matching your search
              </MenuItem>
            )}
          </TextField>
        </Grid>

        {parameters.map((param) => {
          const value = inputParameters[param.name] || "";
          const isValid = param.regex ? new RegExp(param.regex).test(value) : true;
          const isError = param.mandatory && value.length > 0 && !isValid;

          return (
            <Grid item xs={12} md={6} key={param.name}>
              <TextField
                fullWidth
                label={param.desc}
                value={value}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (param.inputType === "NUMERIC" && !/^\d*$/.test(newValue)) return;
                  if (newValue.length > param.maxLength) return;
                  setInputValues({ ...inputParameters, [param.name]: newValue });
                }}
                onBlur={() => {
                  if (param.mandatory && value.length === 0) {
                    toast.error(`${param.desc} is required.`);
                  } else if (!isValid) {
                    toast.error(`Invalid ${param.desc} format.`);
                  }
                }}
                placeholder={`Enter ${param.desc}`}
                inputProps={{
                  minLength: param.minLength,
                  maxLength: param.maxLength,
                }}
                error={isError}
                helperText={isError ? `Invalid ${param.desc}` : ""}
              />
            </Grid>
          );
        })}

        {!billFetch && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Enter Amount"
              type="number"
              value={transactionAmount}
              onChange={(e) => setTransactionAmount(e.target.value)}
              placeholder="Enter amount"
              inputProps={{
                min: 1,
                step: 1
              }}
            />
          </Grid>
        )}
      </Grid>

      <Grid
        item
        xs={12}
        style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}
      >
        <Button
          onClick={billFetch ? handleProceedToRecharge : handlePayNow}
          style={{
            backgroundColor: "#027BFE",
            color: "white",
            textTransform: "none",
          }}
          disabled={loading || !selectedBiller}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : billFetch ? (
            "Proceed to Recharge"
          ) : (
            "Pay Now"
          )}
        </Button>
      </Grid>

      <BillDetailsComponent
        message={message}
        billDetails={billDetails}
        billerId={billerId}
        validateBiller={validateBiller}
        initChannels={initChannels}
        paymentModes={paymentModes}
        service={serviceName}
      />
    </Box>
  );
};

export default GenericUtilityService;